/**
 * PixiApp.ts — Inicializa o Pixi e gerencia as camadas de render.
 *
 * Camadas (de baixo pra cima):
 *   starsLayer (fixo na tela; a Estrelas faz a paralaxe por conta propria)
 *   worldContainer
 *     planetasLayer
 *     tirosLayer
 *     navesLayer
 *     fxLayer      (Fase 6: explosoes e pulsos)
 *     uiLayer      (Fase 6: aneis de selecao, linhas de rota)
 */

import { Application, Container } from 'pixi.js';
import { AdvancedBloomFilter } from 'pixi-filters';

export interface Viewport {
  w: number;
  h: number;
}

export class PixiApp {
  app!: Application;
  starsLayer!: Container;
  worldContainer!: Container;
  planetasLayer!: Container;
  tirosLayer!: Container;
  navesLayer!: Container;
  fxLayer!: Container;
  uiLayer!: Container;
  viewport: Viewport = { w: window.innerWidth, h: window.innerHeight };

  private readonly onResizeCallbacks: Array<() => void> = [];

  async init(container: HTMLElement): Promise<void> {
    this.app = new Application();
    await this.app.init({
      width: window.innerWidth,
      height: window.innerHeight,
      background: 0x050810,
      antialias: true,
      resolution: Math.min(2, window.devicePixelRatio || 1),
      autoDensity: true,
    });
    container.appendChild(this.app.canvas);

    this.starsLayer = new Container();
    this.worldContainer = new Container();
    this.planetasLayer = new Container();
    this.tirosLayer = new Container();
    this.navesLayer = new Container();
    this.fxLayer = new Container();
    this.uiLayer = new Container();

    this.worldContainer.addChild(this.planetasLayer);
    this.worldContainer.addChild(this.tirosLayer);
    this.worldContainer.addChild(this.navesLayer);
    this.worldContainer.addChild(this.fxLayer);
    this.worldContainer.addChild(this.uiLayer);

    this.app.stage.addChild(this.starsLayer);
    this.app.stage.addChild(this.worldContainer);

    // Bloom global suave (so afeta o worldContainer, nao HUD HTML)
    try {
      this.worldContainer.filters = [
        new AdvancedBloomFilter({
          threshold: 0.35,
          bloomScale: 0.7,
          brightness: 1.0,
          blur: 6,
          quality: 4,
        }),
      ];
    } catch (e) {
      console.warn('[SpaceWar] bloom indisponivel:', e);
    }

    window.addEventListener('resize', () => this.handleResize());
  }

  /** Registra callback chamado em resize (usado por Estrelas pra recriar). */
  onResize(cb: () => void): void {
    this.onResizeCallbacks.push(cb);
  }

  /** Adiciona uma funcao ao ticker do Pixi (executada a cada frame). */
  addTicker(fn: (ticker: { deltaTime: number; deltaMS: number }) => void): void {
    this.app.ticker.add(fn);
  }

  /** Coordenadas do canvas (substitui `Pixi.app.canvas` direto). */
  get canvas(): HTMLCanvasElement {
    return this.app.canvas as HTMLCanvasElement;
  }

  private handleResize(): void {
    this.viewport.w = window.innerWidth;
    this.viewport.h = window.innerHeight;
    this.app.renderer.resize(this.viewport.w, this.viewport.h);
    for (const cb of this.onResizeCallbacks) cb();
  }
}
